/* ============================================================
   LIQUID_GOLD: Sitewide word-mask heading reveal
   Splits qualifying headings into per-word masks and slides each word up,
   staggered left-to-right, when its parent section scrolls into view.
   Fires once per page load. All state under window.LiquidGold.headingReveal.

   Load deferred. No dependencies.

   Guiding rule: if anything about a heading is uncertain, leave it unsplit
   and visible. A heading that never animates is a non-event; a heading stuck
   at translateY(140%) is invisible copy.
   ============================================================ */
window.LiquidGold = window.LiquidGold || {};

window.LiquidGold.headingReveal = {
  /* ---------- Configuration ---------- */

  DEFAULTS: {
    scope: 'h1,h2',
    duration: 950, // ms
    stagger: 0.075, // seconds between words
    exclude: '', // extra merchant-supplied selectors
    maxWords: 40, // longer headings read as body copy — skip them
    headingDelay: 0.12, // seconds of extra base delay per heading in a section
    threshold: 0.2,
    safetyMs: 4000,
  },

  /* Skipped when matched by the heading ITSELF or any ancestor.
     Every entry here is a container that can be display:none or detached when
     we initialise — an IntersectionObserver on a hidden ancestor never fires,
     so a split heading inside one would stay permanently invisible. The card /
     grid entries are different: those headings are fine, but a grid of twenty
     product titles all staggering at once reads as noise, not motion. */
  EXCLUDE_SELF_OR_ANCESTOR: [
    '[data-lg-no-split]',
    'details',
    '[role="dialog"]',
    '.modal',
    '.drawer',
    '.cart-drawer',
    '.mega-menu',
    '.quick-add',
    '[hidden]',
    '[aria-hidden="true"]',
    '.card',
    '.grid__item',
    '[class*="product-card"]',
  ].join(', '),

  /* Skipped when matched by an ANCESTOR only — never by the heading itself.
     LIQUID_GOLD: [data-lg-reveal] sections animate their own heading —
     .lg-kino-jrn__title, .lg-kino-vfx__heading and .lg-kino-fdr__heading all
     have their own transform+transition in liquid_gold_kino-clinic.css.
     Splitting those would run two transforms on one element and fight.

     Deliberately NOT listed: .lg-kino-how, .lg-kino-showcase and [data-lg-tst].
     Those sections do choreograph other children (step borders, dashboard
     cards, sparkline), but their headings are plain typography with no
     transform of their own, so excluding the whole section wrongly skipped
     .lg-kino-how__heading, .lg-kino-showcase__heading and .lg-kino-tst__head h2.
     Match on what actually animates, not on the owning section.

     Ancestor-only matters because [data-lg-reveal] is overloaded: it is both
     the kino section-reveal hook AND this feature's opt-in attribute. Testing
     ancestors only lets a heading carry [data-lg-reveal] as an opt-in without
     excluding itself, while a heading *inside* a kino reveal section is still
     correctly left alone. */
  EXCLUDE_ANCESTOR_ONLY: ['[data-lg-reveal]'].join(', '),

  /* Header / footer section groups. Their section ids carry the group key, and
     the landmarks themselves are the belt-and-braces check. */
  EXCLUDE_LANDMARK: 'header, footer, [role="banner"], [role="contentinfo"]',

  config: {},

  _originals: new WeakMap(), // heading -> original innerHTML, for destroy()
  _observers: new WeakMap(), // section -> IntersectionObserver
  _timers: new WeakMap(), // section -> safety timeout id
  _armed: new Set(), // sections awaiting reveal, for the scroll fallback
  _boundFallback: false,
  _varsApplied: false,

  /* ---------- Public API ---------- */

  init(root = document) {
    // Under reduced motion we never split at all — cleaner than splitting and
    // then disabling, and it keeps copy/paste and screen readers untouched.
    if (this._reduced()) return;

    this.config = Object.assign({}, this.DEFAULTS, window.LiquidGold.headingRevealConfig || {});
    this._applyVars();
    this._bindFallback();

    this._sections(root).forEach((section) => this._arm(section));
  },

  reveal(sectionEl) {
    if (!sectionEl) return;

    const headings = this._splitHeadings(sectionEl);
    headings.forEach((heading, n) => {
      if (heading.classList.contains('lg-heading-reveal--in')) return;

      const base = n * this.config.headingDelay;
      const inners = heading.querySelectorAll('.lg-heading-reveal__inner');

      // Only rewrite delays when this heading sits behind others in its
      // section; the first heading keeps the delays set at split time.
      if (base > 0) {
        inners.forEach((inner, k) => {
          inner.style.transitionDelay = (base + k * this.config.stagger).toFixed(3) + 's';
        });
      }

      heading.classList.add('lg-heading-reveal--in');
      this._settle(heading, inners.length, base);
    });

    this._disarm(sectionEl);
  },

  destroy(root = document) {
    this._sections(root).forEach((section) => {
      this._disarm(section);
      delete section.dataset.lgHrArmed;
    });

    const scope = root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-lg-hr-init]').forEach((heading) => {
      const original = this._originals.get(heading);
      // Fall back to the flattened text rather than leaving masks behind — a
      // heading with no stored original is still better off unwrapped.
      heading.innerHTML = original !== undefined ? original : heading.textContent;
      this._originals.delete(heading);
      heading.classList.remove(
        'lg-heading-reveal',
        'lg-heading-reveal--in',
        'lg-heading-reveal--done'
      );
      delete heading.dataset.lgHrInit;
    });
  },

  /* ---------- Arming ---------- */

  _arm(section) {
    // _headings() already filters out anything split, so re-entering a known
    // section is how AJAX-injected content gets picked up.
    this._headings(section).forEach((heading) => this._split(heading));

    // Splitting can decline every candidate (all over maxWords, all hidden).
    if (!this._splitHeadings(section).length) return;

    if (section.dataset.lgHrArmed === 'true') {
      // Still waiting to fire — the live observer covers whatever we just
      // added, so there is nothing more to set up.
      if (this._armed.has(section)) return;

      // LIQUID_GOLD: already fired, then new headings arrived (quick view,
      // infinite scroll, an innerHTML swap). Re-observing a section that has
      // already been on screen would leave them parked below the baseline
      // forever, so reveal them straight away instead.
      this.reveal(section);
      return;
    }

    section.dataset.lgHrArmed = 'true';
    this._armed.add(section);

    if (typeof IntersectionObserver === 'undefined') {
      this.reveal(section);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.reveal(entry.target);
        });
      },
      { threshold: this.config.threshold }
    );
    observer.observe(section);
    this._observers.set(section, observer);

    this._scheduleSafety(section);
  },

  /* LIQUID_GOLD: last line of defence. If the observer never fires for a
     section the visitor can actually see — a transformed ancestor, a section
     too short to satisfy the threshold, a browser quirk — force the reveal
     rather than leave copy invisible.

     It re-checks instead of firing unconditionally. An unconditional timer
     revealed EVERY armed section a few seconds after load, so every heading
     below the fold played its animation off-screen and was already
     --in --done by the time the visitor scrolled to it: no motion on the FAQ,
     feature or showcase headings. A section still below the fold has not
     failed to fire, it just has not been reached, so we wait and look again. */
  _scheduleSafety(section) {
    this._timers.set(
      section,
      window.setTimeout(() => {
        if (!this._armed.has(section)) return;

        // Detached by an AJAX swap — stop tracking it.
        if (section.isConnected === false) {
          this._disarm(section);
          return;
        }

        const vh = window.innerHeight || document.documentElement.clientHeight;
        const rect = section.getBoundingClientRect();
        if (rect.top < vh && rect.bottom > 0) {
          this.reveal(section);
          return;
        }

        this._scheduleSafety(section);
      }, this.config.safetyMs)
    );
  },

  _disarm(section) {
    const observer = this._observers.get(section);
    if (observer) {
      observer.disconnect();
      this._observers.delete(section);
    }
    const timer = this._timers.get(section);
    if (timer) {
      window.clearTimeout(timer);
      this._timers.delete(section);
    }
    this._armed.delete(section);
  },

  /* ---------- Above-the-fold fallback ----------
     A section already in view at load may never produce an intersection
     *change*, so a throttled scroll pass plus two scheduled sweeps cover it. */

  _bindFallback() {
    if (this._boundFallback) return;
    this._boundFallback = true;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        this._sweep();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.requestAnimationFrame(() => this._sweep());
    window.setTimeout(() => this._sweep(), 700);
  },

  _sweep() {
    const vh = window.innerHeight || document.documentElement.clientHeight;
    Array.from(this._armed).forEach((section) => {
      const rect = section.getBoundingClientRect();
      if (rect.top < vh * 0.92 && rect.bottom > 0) this.reveal(section);
    });
  },

  /* ---------- Settling ---------- */

  _settle(heading, wordCount, base) {
    if (!wordCount) return;

    const total =
      (base + (wordCount - 1) * this.config.stagger) * 1000 + this.config.duration + 60;

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      heading.removeEventListener('transitionend', onEnd);
      window.clearTimeout(timer);
      heading.classList.add('lg-heading-reveal--done');
    };

    // The last word's transitionend is the real signal; the timer is there for
    // the case where the transition is interrupted or never fires.
    let seen = 0;
    const onEnd = (e) => {
      if (e.propertyName !== 'transform') return;
      seen += 1;
      if (seen >= wordCount) finish();
    };

    heading.addEventListener('transitionend', onEnd);
    const timer = window.setTimeout(finish, total);
  },

  /* ---------- Selection ---------- */

  _sections(root) {
    if (!root || root === document || root.nodeType === Node.DOCUMENT_NODE) {
      return Array.from(document.querySelectorAll('.shopify-section'));
    }
    // shopify:section:load hands us the section element itself.
    if (root.classList && root.classList.contains('shopify-section')) return [root];

    const nested = root.querySelectorAll ? Array.from(root.querySelectorAll('.shopify-section')) : [];
    // An AJAX-injected container (quick view, infinite scroll) has no section
    // wrapper of its own — treat the container as one so it still animates.
    return nested.length ? nested : root.nodeType === Node.ELEMENT_NODE ? [root] : [];
  },

  _headings(section) {
    if (this._sectionExcluded(section)) return [];

    const selector = this._scopeSelector();
    if (!selector) return [];

    return Array.from(section.querySelectorAll(selector)).filter((h) => this._eligible(h));
  },

  _splitHeadings(section) {
    return Array.from(section.querySelectorAll('[data-lg-hr-init]'));
  },

  _scopeSelector() {
    switch (this.config.scope) {
      case 'h2':
        return 'h2';
      case 'optin':
        // Opt-in: the attribute must sit on the heading itself.
        return 'h1[data-lg-reveal], h2[data-lg-reveal], h3[data-lg-reveal], h4[data-lg-reveal]';
      case 'h1,h2':
      default:
        return 'h1, h2';
    }
  },

  _sectionExcluded(section) {
    const id = section.id || '';
    if (/header|footer/i.test(id)) return true;
    return !!(section.closest && section.closest(this.EXCLUDE_LANDMARK));
  },

  _eligible(heading) {
    if (heading.dataset.lgHrInit) return false;
    if (heading.closest(this.EXCLUDE_SELF_OR_ANCESTOR)) return false;
    if (heading.closest(this.EXCLUDE_LANDMARK)) return false;

    const parent = heading.parentElement;
    if (parent && parent.closest(this.EXCLUDE_ANCESTOR_ONLY)) return false;

    const extra = (this.config.exclude || '').trim();
    if (extra) {
      try {
        if (heading.closest(extra)) return false;
      } catch (e) {
        // A malformed merchant selector must not take the whole feature down.
      }
    }

    // offsetParent === null means display:none or a detached subtree — the
    // observer would never fire and the copy would stay hidden. Note this is
    // also null for position:fixed elements, which is fine: a fixed heading is
    // exactly the kind of uncertainty we decline.
    if (heading.offsetParent === null) return false;

    const text = (heading.textContent || '').trim();
    if (!text) return false;
    if (text.split(/\s+/).length > this.config.maxWords) return false;

    return true;
  },

  /* ---------- Splitting ---------- */

  _split(heading) {
    if (heading.dataset.lgHrInit) return;

    this._originals.set(heading, heading.innerHTML);

    const frag = document.createDocumentFragment();
    const state = { k: 0 };
    this._walk(heading, frag, state);

    // Nothing splittable (an icon-only heading, an all-link heading) — put it
    // back exactly as it was and leave it visible.
    if (!state.k) {
      this._originals.delete(heading);
      return;
    }

    heading.innerHTML = '';
    heading.appendChild(frag);
    heading.classList.add('lg-heading-reveal');
    heading.dataset.lgHrInit = 'true';
  },

  _walk(node, out, state) {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        this._splitText(child.nodeValue, out, state);
        return;
      }

      if (child.nodeType !== Node.ELEMENT_NODE) {
        out.appendChild(child.cloneNode(true));
        return;
      }

      const tag = child.tagName;

      // <br> is structural — carry it through untouched.
      if (tag === 'BR') {
        out.appendChild(child.cloneNode(false));
        return;
      }

      // LIQUID_GOLD: never split link text. Masking words inside an <a> breaks
      // the link's hit area into disjoint boxes and mangles focus rings.
      if (tag === 'A') {
        out.appendChild(child.cloneNode(true));
        return;
      }

      // Inline wrappers (<em>, <strong>, <span>) are preserved and recursed
      // into, so their styling still applies to the words inside them.
      const clone = child.cloneNode(false);
      this._walk(child, clone, state);
      out.appendChild(clone);
    });
  },

  _splitText(text, out, state) {
    if (!text) return;

    text.split(/(\s+)/).forEach((part) => {
      if (part === '') return;

      if (/^\s+$/.test(part)) {
        // LIQUID_GOLD: a real text node, not a span. This is what preserves
        // native line breaking, word-spacing, copy/paste and the accessibility
        // tree — a heading still reads and copies as "one two three".
        out.appendChild(document.createTextNode(' '));
        return;
      }

      const word = document.createElement('span');
      word.className = 'lg-heading-reveal__word';

      const inner = document.createElement('span');
      inner.className = 'lg-heading-reveal__inner';
      inner.textContent = part;
      inner.style.transitionDelay = (state.k * this.config.stagger).toFixed(3) + 's';

      word.appendChild(inner);
      out.appendChild(word);
      state.k += 1;
    });
  },

  /* ---------- Helpers ---------- */

  _applyVars() {
    if (this._varsApplied) return;
    this._varsApplied = true;

    const root = document.documentElement;
    if (this.config.duration) root.style.setProperty('--lg-hr-duration', this.config.duration + 'ms');
    if (this.config.stagger || this.config.stagger === 0) {
      root.style.setProperty('--lg-hr-stagger', this.config.stagger + 's');
    }
  },

  _reduced() {
    return !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  },
};

/* ---------- Boot + theme-editor hooks ---------- */
(function () {
  const hr = window.LiquidGold.headingReveal;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => hr.init());
  } else {
    hr.init();
  }

  document.addEventListener('shopify:section:load', (e) => hr.init(e.target));
  document.addEventListener('shopify:section:select', (e) => {
    hr.destroy(e.target);
    hr.init(e.target);
  });
})();
